import React from 'react';

import useContextApi from './_use-context-singleton';
import { ALERT_SEVERITY_ENUM, FREQUENCY_ENUM } from '../enums';
import * as helpers from '../helpers';
import * as types from '../types';
import { BaseStore } from '../types';

type ResourceWithKeepCurrent = {
  id?: number;
  shared?: boolean;
  end_date_latest?: boolean;
  organization_id?: number;
};

type UseKeepCurrentOptions<TResource extends ResourceWithKeepCurrent> = {
  resourceType: keyof BaseStore['resources'];
  setResource: (_resource: TResource) => void;
  setAlert?: (_alert: types.common.Alert | null) => void;
  getApiParams: (_resource: TResource) => {
    countries?: string[];
    universe_id?: number;
    frequency?: FREQUENCY_ENUM;
  };
};

/**
 * Hook that provides a keepCurrent function to update resource dates
 *
 * @template TResource Type of the resource that extends ResourceWithKeepCurrent
 * @param options.resource - The resource to keep current (must have shared, end_date_latest, organization_id)
 * @param options.setResource - Function to update the resource after successful keep current
 * @param options.setAlert - Optional function to set alert messages for errors
 * @param options.resourceType - The resource type string (e.g., 'universes', 'signal_sets')
 * @param options.getApiParams - Function to extract API parameters from the resource
 * @returns Object containing the keepCurrent async function
 */
const useKeepCurrent = <TResource extends ResourceWithKeepCurrent>({
  setAlert,
  setResource,
  resourceType,
  getApiParams,
}: UseKeepCurrentOptions<TResource>) => {
  const { api, features } = useContextApi();

  /**
   * Updates resource dates to keep them current
   *
   * @param resource - The resource to keep current
   * @param update - If true, will update the resource flag to true and keep current
   * @returns The updated resource or null if update was not performed
   */
  const keepCurrent = React.useCallback(async (resource: TResource, update = false): Promise<void> => {
    if (!resource.id) return;
    if (resource.shared) return;
    if (!resource.end_date_latest && !update) return;
    if (resource.organization_id === 0 || !features.keep_resources_current) return;

    try {
      const apiParams = getApiParams(resource);
      const resp = await api.dates.keep_current({
        resource_id: resource.id,
        resource_type: resourceType,
        max_date: features.end_date || undefined,
        ...apiParams,
      });

      const updatedResource = resp.resource as TResource;
      setResource(updatedResource);
    } catch (error) {
      if (setAlert) {
        setAlert({
          severity: ALERT_SEVERITY_ENUM.ERROR,
          message: `Error updating resource dates - ${helpers.parseApiError(error as types.common.ApiError)}`,
        });
      }
      return null;
    }
  }, []);

  return {
    keepCurrent,
  };
};

export default useKeepCurrent;
